import { useState, useEffect, useRef, Dispatch, SetStateAction } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '../../../core/api/client';
import { useAuth } from '../../authentication/hooks/useAuth';
import { activeWorkspaceStore } from '@/shared/workspace/activeWorkspaceStore';
import { resolveMessageTarget } from '../utils/resolveMessageTarget';
import { getFirstContactEligibility } from '../api/messageApi';
import { Conversation } from '../types';
import { useConversations } from './useMessages';

type ProfileViewCorrelation = { recipientId: string; engagementId: string } | null;

export type UseMessageLinkRecipientResult = {
  selectedConversation: Conversation | null;
  setSelectedConversation: Dispatch<SetStateAction<Conversation | null>>;
  showChat: boolean;
  setShowChat: Dispatch<SetStateAction<boolean>>;
  isLoadingRecipient: boolean;
  linkError: string | null;
  getProfileViewEngagementId: (participantId: string) => string | undefined;
  clearProfileViewCorrelation: () => void;
  /**
   * Authoritative-only for `serviceProviderId` deep links: `true` once the
   * eligibility endpoint confirms the FA→SP pair has no messages yet (spec
   * ticket 10 §6.4). Always `false` for an existing/list-selected
   * conversation and for the legacy `recipientUserId`/`recipient` paths,
   * which keep today's list-absence heuristic untouched.
   */
  isNewConversation: boolean;
  setIsNewConversation: Dispatch<SetStateAction<boolean>>;
};

/**
 * Resolves the `serviceProviderId` / `recipientUserId` (or legacy `recipient`)
 * URL parameter into a conversation, opening an existing one or preparing a
 * new draft. Spec §3.1: the two identifiers are mutually exclusive; an
 * ambiguous link fails closed with a visible error instead of guessing.
 */
export function useMessageLinkRecipient(): UseMessageLinkRecipientResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [showChat, setShowChat] = useState(false);
  const [isLoadingRecipient, setIsLoadingRecipient] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [profileViewCorrelation, setProfileViewCorrelation] = useState<ProfileViewCorrelation>(null);
  const [isNewConversation, setIsNewConversation] = useState(false);

  // Track if we've already handled the recipient parameter to prevent loops
  const processedRecipientRef = useRef<string | null>(null);

  const { data: conversationsData, isLoading: isLoadingConversations } = useConversations(1, 50);

  useEffect(() => {
    const target = resolveMessageTarget({
      serviceProviderId: searchParams.get('serviceProviderId'),
      recipientUserId: searchParams.get('recipientUserId'),
      recipient: searchParams.get('recipient'),
    });
    const profileViewEngagementId = searchParams.get('profileViewEngagementId');

    if (target === 'ambiguous') {
      if (processedRecipientRef.current !== 'ambiguous') {
        processedRecipientRef.current = 'ambiguous';
        setLinkError("This message link isn't valid — it points to more than one recipient.");
        setSearchParams({}, { replace: true });
      }
      return;
    }

    // Key on the raw target + engagementId so reopening the same target with a
    // different (or missing) profileViewEngagementId isn't skipped as a dupe —
    // otherwise the conversion correlation would go stale/missing.
    const rawTargetId = target?.kind === 'serviceProvider' ? target.serviceProviderId : target?.recipientUserId;
    const processedKey = rawTargetId ? `${rawTargetId}:${profileViewEngagementId ?? ''}` : null;

    // Skip if no target, no user, still loading, or already processed this combo
    if (!target || !user || isLoadingConversations || processedRecipientRef.current === processedKey) {
      return;
    }
    processedRecipientRef.current = processedKey;

    const openConversationFor = async () => {
      try {
        setIsLoadingRecipient(true);
        setLinkError(null);

        // The message page — not the CTA — resolves serviceProviderId to the
        // SP's user id via the existing provider-lookup contract, so every
        // SP-targeting CTA can emit the same param regardless of entry point.
        const recipientId =
          target.kind === 'serviceProvider'
            ? (await apiClient.get<{ user: { id: string } }>(`/providers/${target.serviceProviderId}/user-id`))
                .data.user.id
            : target.recipientUserId;

        if (profileViewEngagementId) {
          setProfileViewCorrelation({ recipientId, engagementId: profileViewEngagementId });
        } else {
          // Reopening this recipient without an engagementId — drop any stale
          // correlation from an earlier profile-view link so it isn't misapplied.
          setProfileViewCorrelation(null);
        }

        // Check if conversation already exists
        const existingConversation = conversationsData?.data.find(
          (conv) => conv.participant.id === recipientId
        );

        if (existingConversation) {
          setSelectedConversation(existingConversation);
          setIsNewConversation(false);
          setShowChat(true);
          // Clear the URL parameter without pushing a new history entry,
          // so the browser Back button returns to the page that linked here
          setSearchParams({}, { replace: true });
          return;
        }

        // Absent from the local list — not authoritative on its own (a pair's
        // messages can simply be off the loaded page). For an FA→SP
        // `serviceProviderId` link, confirm with the eligibility endpoint
        // before treating this as genuinely new (spec ticket 10 §6.4). The
        // legacy `recipientUserId`/`recipient` paths have no equivalent
        // endpoint, so they never confirm "new" — `ChatInterface` keeps
        // today's spinner/403-404-folding behavior for them, unchanged.
        let confirmedNew = false;
        if (target.kind === 'serviceProvider') {
          try {
            const eligibility = await getFirstContactEligibility(target.serviceProviderId);
            confirmedNew = eligibility.requiresIntent;
          } catch (eligibilityError) {
            // Fail safe: treat as existing/uncertain rather than skip the
            // spinner/error handling for a pair we couldn't actually confirm
            // is new (spec §3.5 — never allow a stale read to misclassify).
            console.error('Failed to read first-contact eligibility:', eligibilityError);
            confirmedNew = false;
          }
        }

        // No existing conversation, create a temporary one for new message
        const response = await apiClient.get(`/users/${recipientId}`);
        const recipientUser = response.data;

        // Construct full name from firstName and lastName
        const recipientName = recipientUser.lastName
          ? `${recipientUser.firstName} ${recipientUser.lastName}`
          : recipientUser.firstName;

        // Conversation ids are keyed on the WORKSPACE OWNER id. For a
        // sub-delegate that is the active workspace owner, not their own
        // user id; non-delegates fall back to their own id (unchanged).
        const selfWorkspaceId = activeWorkspaceStore.get() ?? user.id;
        const conversationId = [selfWorkspaceId, recipientId].sort().join('_');

        // Create temporary conversation object
        const newConversation: Conversation = {
          conversationId,
          participant: {
            id: recipientUser.id,
            name: recipientName,
            role: recipientUser.role,
            profilePhoto: recipientUser.profilePhoto,
            // Carries the already-resolved SP id forward so the chat surface's
            // defensive intent fallback (ticket 08 §3.5) can reopen the modal
            // without needing to re-derive it from the (by-now cleared) URL.
            ...(target.kind === 'serviceProvider' ? { providerId: target.serviceProviderId } : {}),
          },
          lastMessage: {
            content: '',
            timestamp: new Date(),
            senderId: selfWorkspaceId,
          },
          unreadCount: 0,
        };

        setSelectedConversation(newConversation);
        setIsNewConversation(confirmedNew);
        setShowChat(true);
        // Clear the URL parameter without pushing a new history entry,
        // so the browser Back button returns to the page that linked here
        setSearchParams({}, { replace: true });
      } catch (error) {
        console.error('Failed to resolve message recipient:', error);
        setLinkError("We couldn't open this conversation. The link may be invalid or out of date.");
        setIsNewConversation(false);
        // Clear the URL parameter even on error
        setSearchParams({}, { replace: true });
      } finally {
        setIsLoadingRecipient(false);
      }
    };

    openConversationFor();
  }, [searchParams, user, isLoadingConversations, conversationsData?.data, setSearchParams]);

  // Reset processed recipient when searchParams change (user navigated away and back)
  useEffect(() => {
    const hasTarget =
      searchParams.get('serviceProviderId') ||
      searchParams.get('recipientUserId') ||
      searchParams.get('recipient');
    if (!hasTarget) {
      processedRecipientRef.current = null;
    }
  }, [searchParams]);

  const getProfileViewEngagementId = (participantId: string): string | undefined =>
    profileViewCorrelation?.recipientId === participantId ? profileViewCorrelation.engagementId : undefined;

  const clearProfileViewCorrelation = () => setProfileViewCorrelation(null);

  return {
    selectedConversation,
    setSelectedConversation,
    showChat,
    setShowChat,
    isLoadingRecipient,
    linkError,
    getProfileViewEngagementId,
    clearProfileViewCorrelation,
    isNewConversation,
    setIsNewConversation,
  };
}
